import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { SocialSidebar } from '@/components/feature/SocialSidebar';

// ── Mock reviews ──────────────────────────────────────────────────────────────
const existingReviews = [
  {
    id: 1,
    name: 'Samira V.',
    location: 'Amsterdam',
    rating: 5,
    product: 'Blue Protect Soap',
    date: '12 mrt 2026',
    comment: 'Echt fantastisch! Ik voel me na gebruik veel lichter en beschermd. De geur is heerlijk en de kwaliteit is top. Gebruik het nu elke ochtend als ritueel.',
    avatar: 'https://public.readdy.ai/ai/img_res/a48a9cb96b29a7f5a7845e816795897e.jpg',
    verified: true,
  },
  {
    id: 2,
    name: 'Yasmine A.',
    location: 'Rotterdam',
    rating: 5,
    product: 'Evil Energy Destroy Spray',
    date: '18 mrt 2026',
    comment: 'Altijd in mijn tas. Na bezoek aan drukke plekken spray ik mezelf en de auto. Werkt direct — ik voel het verschil meteen. Absolute aanrader voor iedereen die gevoelig is voor energie.',
    avatar: 'https://readdy.ai/api/search-image?query=woman%20portrait%20warm%20golden%20light%20natural%20smile%20confident%20expression%20dark%20background%20minimal&width=80&height=80&seq=rev-av2&orientation=squarish',
    verified: true,
  },
  {
    id: 3,
    name: 'Habiba M.',
    location: 'Den Haag',
    rating: 5,
    product: 'Evil Energy Destroy Kit',
    date: '20 mrt 2026',
    comment: 'Het bad met het poeder was een gamechanger. Na één keer al voelde de energie in huis compleet anders. Mijn kinderen slapen eindelijk rustig. Ik kan dit niet genoeg aanraden.',
    avatar: 'https://readdy.ai/api/search-image?query=woman%20portrait%20soft%20light%20natural%20beauty%20serene%20calm%20expression%20dark%20background%20minimal&width=80&height=80&seq=rev-av3&orientation=squarish',
    verified: true,
  },
  {
    id: 4,
    name: 'Fatima O.',
    location: 'Utrecht',
    rating: 5,
    product: 'Blue Protect Balls',
    date: '10 mrt 2026',
    comment: 'Geweldig product. Ik merk direct het verschil in mijn slaapkamer. De ballen in een glas water met cologne — precies zoals beschreven. Sfeer is zoveel rustiger geworden.',
    avatar: 'https://readdy.ai/api/search-image?query=young%20woman%20portrait%20natural%20light%20warm%20tones%20calm%20expression%20dark%20background%20minimal&width=80&height=80&seq=rev-av4&orientation=squarish',
    verified: true,
  },
  {
    id: 5,
    name: 'Rania K.',
    location: 'Eindhoven',
    rating: 5,
    product: 'Evil Energy Destroy Zeep',
    date: '22 mrt 2026',
    comment: 'Na maanden van aanhoudende last is dit het enige dat echt heeft geholpen. Ik slaap eindelijk weer goed. De zeep gebruik ik dagelijks en het ritueel werkt echt.',
    avatar: 'https://readdy.ai/api/search-image?query=woman%20portrait%20soft%20warm%20light%20natural%20beauty%20confident%20expression%20dark%20background%20minimal&width=80&height=80&seq=rev-av5&orientation=squarish',
    verified: true,
  },
  {
    id: 6,
    name: 'Miriam S.',
    location: 'Breda',
    rating: 5,
    product: 'Evil Energy Destroy Cologne',
    date: '17 mrt 2026',
    comment: 'Gebruik dit elke week om mijn huis te dweilen. De sfeer is daarna compleet anders — lichter en beschermd. In bad met dit cologne is een compleet ritueel op zich.',
    avatar: 'https://readdy.ai/api/search-image?query=woman%20portrait%20natural%20light%20calm%20serene%20expression%20dark%20background%20minimal&width=80&height=80&seq=rev-av6&orientation=squarish',
    verified: true,
  },
];

const PRODUCTS = [
  'Blue Protect Soap',
  'Blue Protect Balls (5 Balls)',
  'Blue Protect Powder',
  'Blue Protect Spray (120ml)',
  'Evil Energy Destroy Zeep',
  'Evil Energy Destroy Baby Hing Pasta',
  'Evil Energy Destroy Hing Poeder',
  'Evil Energy Destroy Hing Olie',
  'Evil Energy Destroy Spray',
  'Evil Energy Destroy Cologne',
  'Evil Energy Destroy Kit Compleet',
  'Spiritueel Consult',
  'Ritueel Blueprint',
];

// ── Star Selector ─────────────────────────────────────────────────────────────
const StarSelector = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="w-9 h-9 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
        >
          <i className={`ri-star-fill text-2xl transition-colors ${
            star <= (hovered || value) ? 'text-[#d4a017]' : 'text-white/15'
          }`} />
        </button>
      ))}
      {value > 0 && (
        <span className="ml-2 text-white/50 text-sm">
          {value === 5 ? 'Uitstekend' : value === 4 ? 'Goed' : value === 3 ? 'Gemiddeld' : value === 2 ? 'Matig' : 'Slecht'}
        </span>
      )}
    </div>
  );
};

// ── Review Card ───────────────────────────────────────────────────────────────
const ReviewCard = ({ review, featured = false }: { review: typeof existingReviews[0]; featured?: boolean }) => (
  <div className={`flex flex-col gap-4 p-6 rounded-2xl border transition-all duration-300 ${
    featured
      ? 'border-violet-500/40 bg-violet-600/8'
      : 'border-violet-500/12 bg-white/2 hover:border-violet-500/28'
  }`}>
    <div className="flex items-center gap-0.5">
      {[...Array(review.rating)].map((_, i) => (
        <i key={i} className="ri-star-fill text-[#d4a017] text-sm" />
      ))}
    </div>
    <p className="text-white/65 text-sm leading-relaxed flex-1">&ldquo;{review.comment}&rdquo;</p>
    <div className="inline-flex items-center gap-1.5 self-start px-3 py-1 bg-violet-600/15 border border-violet-500/20 rounded-full">
      <i className="ri-shopping-bag-line text-violet-400 text-xs" />
      <span className="text-violet-300/80 text-xs font-medium">{review.product}</span>
    </div>
    <div className="flex items-center gap-3 pt-3 border-t border-white/6">
      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-violet-500/20">
        <img src={review.avatar} alt={review.name} className="w-full h-full object-cover object-top" />
      </div>
      <div>
        <p className="text-white font-semibold text-sm">{review.name}</p>
        <p className="text-white/35 text-xs">{review.location} · {review.date}</p>
      </div>
      {review.verified && (
        <div className="ml-auto w-6 h-6 flex items-center justify-center rounded-full bg-violet-500/20 shrink-0" title="Geverifieerde aankoop">
          <i className="ri-check-line text-violet-400 text-xs" />
        </div>
      )}
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const ReviewsPage = () => {
  useSEO({
    title: 'Reviews & Ervaringen | ZpiritualZ Den Haag – Spirituele Beschermingsproducten',
    description: 'Lees echte ervaringen van ZpiritualZ klanten over onze spirituele beschermingsproducten. 5-sterren beoordelingen voor Blue Protect & Evil Energy Destroy. Deel ook jouw eigen ervaring.',
    canonical: 'https://www.zpiritualz.com/reviews',
    keywords: 'ZpiritualZ reviews, spirituele producten ervaringen, blue protect review, evil energy destroy review, hing zeep ervaringen',
    ogImage: 'https://readdy.ai/api/search-image?query=spiritual%20testimonials%20reviews%20stars%20glowing%20violet%20purple%20dark%20mystical%20background%20customer%20experiences%20spiritual%20products%20protection%20energy&width=1200&height=630&seq=og-reviews1&orientation=landscape',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Reviews & Ervaringen — ZpiritualZ',
        description: 'Echte ervaringen van klanten over ZpiritualZ spirituele beschermingsproducten.',
        url: 'https://www.zpiritualz.com/reviews',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.zpiritualz.com/' },
            { '@type': 'ListItem', position: 2, name: 'Reviews', item: 'https://www.zpiritualz.com/reviews' },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: 'ZpiritualZ',
        url: 'https://www.zpiritualz.com',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '5.0',
          reviewCount: String(existingReviews.length),
          bestRating: '5',
          worstRating: '1',
        },
        review: existingReviews.map(r => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.name },
          reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5', worstRating: '1' },
          reviewBody: r.comment,
          datePublished: r.date,
          name: r.product,
        })),
      },
    ],
  });

  const [rating, setRating] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [filterRating, setFilterRating] = useState(0);
  const [filterProduct, setFilterProduct] = useState('');

  const filteredReviews = existingReviews.filter(r => {
    if (filterRating > 0 && r.rating !== filterRating) return false;
    if (filterProduct && r.product !== filterProduct) return false;
    return true;
  });

  const avgRating = (existingReviews.reduce((s, r) => s + r.rating, 0) / existingReviews.length).toFixed(1);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (rating === 0) return;

    const form = e.target as HTMLFormElement;
    const data = new FormData(form);

    if ((data.get('review') as string).length > 500) return;

    setSubmitStatus('sending');

    const body = new URLSearchParams({
      name: data.get('name') as string,
      email: data.get('email') as string,
      product: data.get('product') as string,
      rating: String(rating),
      review: data.get('review') as string,
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d77fliibj7ht1ofp4k80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setRating(0);
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#050010] pt-20">
      <SocialSidebar />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <div className="relative py-24 text-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=spiritual%20testimonials%20reviews%20stars%20glowing%20violet%20purple%20dark%20mystical%20background%20customer%20experiences%20spiritual%20products%20protection%20energy%20ethereal%20atmosphere&width=1920&height=600&seq=reviews-hero1&orientation=landscape"
            alt="Reviews"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/75 via-[#050010]/60 to-[#050010]" />
        </div>
        <div className="relative z-10 px-6">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">✦ Echte Ervaringen ✦</p>
          <h1 className="text-5xl font-black text-white mb-4">
            Reviews &{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              Ervaringen
            </span>
          </h1>
          <p className="text-white/55 text-base max-w-xl mx-auto mb-10">
            Wat klanten zeggen over onze spirituele beschermingsproducten. Geen scripts, geen filters — alleen echte verhalen.
          </p>

          {/* Stats */}
          <div className="inline-flex flex-wrap items-center justify-center gap-8 px-10 py-6 bg-violet-600/8 border border-violet-500/20 rounded-2xl">
            <div className="text-center">
              <p className="text-5xl font-black text-white mb-1">{avgRating}</p>
              <div className="flex items-center justify-center gap-0.5 mb-1">
                {[...Array(5)].map((_, i) => <i key={i} className="ri-star-fill text-[#d4a017] text-sm" />)}
              </div>
              <p className="text-white/35 text-xs">Gemiddelde score</p>
            </div>
            <div className="h-12 w-px bg-white/8 hidden sm:block" />
            <div className="text-center">
              <p className="text-5xl font-black text-white mb-1">{existingReviews.length}+</p>
              <p className="text-white/35 text-xs mt-1">Beoordelingen</p>
            </div>
            <div className="h-12 w-px bg-white/8 hidden sm:block" />
            <div className="text-center">
              <p className="text-5xl font-black text-white mb-1">100%</p>
              <p className="text-white/35 text-xs mt-1">Zou het aanraden</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">

        {/* ── REVIEW FORMULIER ──────────────────────────────────────── */}
        <div className="max-w-2xl mx-auto mb-20">
          <div className="bg-[#110020]/70 border border-violet-500/20 rounded-3xl p-8 lg:p-10">
            <div className="text-center mb-8">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-violet-600/30 to-fuchsia-600/30 border border-violet-500/30 mx-auto mb-4">
                <i className="ri-star-smile-line text-violet-400 text-2xl" />
              </div>
              <h2 className="text-2xl font-black text-white mb-2">Deel Jouw Ervaring</h2>
              <p className="text-white/45 text-sm">
                Jouw review helpt anderen de juiste keuze te maken. We lezen elke review persoonlijk.
              </p>
            </div>

            {submitStatus === 'success' ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-violet-500/20 border border-violet-500/30 mx-auto mb-5">
                  <i className="ri-check-double-line text-3xl text-violet-400" />
                </div>
                <h3 className="text-white font-bold text-xl mb-3">Bedankt voor je review!</h3>
                <p className="text-white/45 text-sm max-w-xs mx-auto mb-6">
                  We hebben je ervaring ontvangen. Na een korte controle wordt hij zichtbaar op deze pagina.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="px-6 py-3 border border-violet-500/30 text-violet-400 rounded-xl text-sm hover:bg-violet-500/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Nog een review schrijven
                </button>
              </div>
            ) : (
              <form
                id="review-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Naam + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/50 text-xs mb-2">Jouw naam *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="Luna de Vries"
                      className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/50 text-xs mb-2">E-mailadres *</label>
                    <input
                      name="email"
                      type="email"
                      required
                      placeholder="jij@email.nl"
                      className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors"
                    />
                  </div>
                </div>

                {/* Product */}
                <div>
                  <label className="block text-white/50 text-xs mb-2">Over welk product? *</label>
                  <div className="relative">
                    <select
                      name="product"
                      required
                      className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white focus:outline-none focus:border-violet-500/50 transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-[#110020]">Kies een product...</option>
                      {PRODUCTS.map(p => (
                        <option key={p} value={p} className="bg-[#110020]">{p}</option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                      <i className="ri-arrow-down-s-line text-violet-400/60" />
                    </div>
                  </div>
                </div>

                {/* Sterren */}
                <div>
                  <label className="block text-white/50 text-xs mb-3">Jouw beoordeling *</label>
                  <StarSelector value={rating} onChange={setRating} />
                  {rating === 0 && submitStatus === 'error' && (
                    <p className="text-rose-400 text-xs mt-1">Geef een beoordeling</p>
                  )}
                </div>

                {/* Review tekst */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-white/50 text-xs">Jouw ervaring *</label>
                    <span className={`text-xs ${charCount > 450 ? 'text-rose-400' : 'text-white/25'}`}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="review"
                    required
                    rows={4}
                    maxLength={500}
                    onChange={e => setCharCount(e.target.value.length)}
                    placeholder="Vertel anderen over jouw ervaring met dit product. Hoe lang gebruik je het al? Welk effect merk je?"
                    className="w-full px-4 py-3 bg-white/5 border border-violet-500/15 rounded-xl text-sm text-white placeholder-white/25 focus:outline-none focus:border-violet-500/50 transition-colors resize-none"
                  />
                </div>

                {/* Privacy note */}
                <p className="text-white/25 text-xs">
                  <i className="ri-shield-check-line mr-1 text-violet-400/60" />
                  Je e-mailadres wordt niet gepubliceerd. Reviews worden na controle zichtbaar.
                </p>

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
                    <i className="ri-error-warning-line text-rose-400 text-sm" />
                    <p className="text-rose-400 text-sm">Er is iets misgegaan. Probeer het opnieuw.</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'sending' || rating === 0}
                  className="w-full py-4 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-bold rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitStatus === 'sending' ? (
                    <><i className="ri-loader-4-line animate-spin" /> Versturen...</>
                  ) : (
                    <><i className="ri-send-plane-line" /> Review Versturen</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* ── BESTAANDE REVIEWS ─────────────────────────────────────── */}
        <div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <h2 className="text-2xl font-bold text-white">
              Alle Reviews
              <span className="ml-3 text-sm font-normal text-white/35">({existingReviews.length})</span>
            </h2>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              {/* Sterren filter */}
              <div className="flex items-center gap-1.5 p-1 bg-[#110020]/60 border border-violet-500/15 rounded-xl">
                <button
                  onClick={() => setFilterRating(0)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    filterRating === 0 ? 'bg-violet-600/30 text-violet-300 border border-violet-500/30' : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  Alle
                </button>
                {[5, 4, 3].map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterRating(filterRating === s ? 0 : s)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      filterRating === s ? 'bg-violet-600/30 text-violet-300 border border-violet-500/30' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    <i className="ri-star-fill text-[#d4a017] text-xs" />
                    {s}
                  </button>
                ))}
              </div>

              {/* Product filter */}
              <div className="relative">
                <select
                  value={filterProduct}
                  onChange={e => setFilterProduct(e.target.value)}
                  className="pl-3 pr-8 py-2 bg-[#110020]/60 border border-violet-500/15 rounded-xl text-xs text-white/60 focus:outline-none focus:border-violet-500/40 appearance-none cursor-pointer"
                >
                  <option value="" className="bg-[#110020]">Alle producten</option>
                  {PRODUCTS.map(p => (
                    <option key={p} value={p} className="bg-[#110020]">{p}</option>
                  ))}
                </select>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 flex items-center justify-center pointer-events-none">
                  <i className="ri-arrow-down-s-line text-violet-400/60 text-xs" />
                </div>
              </div>
            </div>
          </div>

          {filteredReviews.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-violet-600/10 border border-violet-500/15 mx-auto mb-4">
                <i className="ri-search-line text-violet-400 text-xl" />
              </div>
              <p className="text-white/40 text-sm">Geen reviews gevonden voor deze filter.</p>
              <button
                onClick={() => { setFilterRating(0); setFilterProduct(''); }}
                className="mt-4 text-violet-400 text-sm hover:text-violet-300 transition-colors cursor-pointer"
              >
                Filters wissen
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredReviews.map((review, i) => (
                <ReviewCard key={review.id} review={review} featured={i === 0 && filterRating === 0 && !filterProduct} />
              ))}
            </div>
          )}
        </div>

        {/* ── CTA ───────────────────────────────────────────────────── */}
        <div className="mt-20 text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-8 bg-[#110020]/60 border border-violet-500/15 rounded-3xl">
            <div className="text-left">
              <p className="text-white font-bold text-lg mb-1">Nog geen klant?</p>
              <p className="text-white/45 text-sm">Ontdek onze spirituele beschermingsproducten en ervaar het zelf.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold text-sm rounded-xl whitespace-nowrap hover:opacity-90 transition-opacity cursor-pointer"
              >
                <i className="ri-store-2-line" />
                Naar de Shop
              </Link>
              <Link
                to="/consultations"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-violet-500/20 text-white/70 font-medium text-sm rounded-xl whitespace-nowrap hover:bg-white/8 transition-colors cursor-pointer"
              >
                <i className="ri-calendar-line" />
                Consult Boeken
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewsPage;
